// The schema and the TipTap extension list, shared by the screen's live editor
// and headless code on the server (markdown round trips, Y.Doc updates, static
// HTML). `getSchema()` builds a plain ProseMirror schema from the same list, so
// the server needs no DOM and no `Editor`.
//
// Built on `@tiptap/markdown`: every node maps its own markdown tokens.
//
// Profiles:
// - "document": a document body. Top-level blocks carry block ids (the
//   `blockId` attribute); the screen draws `ui` code blocks as interactive UI.
// - "note": a Case's context field. No block ids; the screen limits its length.
// Both read and write the same markdown dialect.
//
// This module holds what changes the schema or the markdown. Behaviour that only
// the live editor needs (id assignment, list splitting, footnote ordering,
// keeping the view still) is in live.ts.

import {
  type AnyExtension,
  getSchema,
  type JSONContent,
  Node,
} from "@tiptap/core";
import { CodeBlock } from "@tiptap/extension-code-block";
import { TableRow } from "@tiptap/extension-table";
import type { Schema } from "@tiptap/pm/model";
import StarterKit from "@tiptap/starter-kit";
import { BlockIdAttributes } from "./block-id";
import { Callout, CalloutContent, CalloutTitle } from "./callout";
import { Details, DetailsContent, DetailsSummary } from "./details";
import { TrameriaFence } from "./fences";
import { TrameriaFile, TrameriaFileInline } from "./file";
import { FootnoteDef, FootnoteRef } from "./footnotes";
import { HomeEnd } from "./home-end";
import { TrameriaListItem } from "./list-item";
import { OpaqueBlock } from "./opaque";
import { TrameriaOrderedList } from "./ordered-list";
import { renderBlockSiblings } from "./render-blocks";
import {
  HtmlBreak,
  TableCellDrag,
  TableOptions,
  TrameriaTable,
  TrameriaTableCell,
  TrameriaTableHeader,
} from "./table";
import { TaskListInput } from "./task-input";
import { TrameriaTaskItem } from "./task-item";
import { TrameriaTaskList } from "./task-list";
import { ToggleInput } from "./toggle-input";
import { TrameriaLink } from "./trameria-link";

/** Which body the editor edits (see the top of this file). */
export type EditorProfile = "document" | "note";

export const EDITOR_PROFILES: readonly EditorProfile[] = ["document", "note"];

/**
 * The doc node. Replaces StarterKit's to control how top-level blocks are
 * joined into markdown (`renderBlockSiblings`: adjacent tables and same-type
 * lists), plus two things only the doc needs:
 *
 * 1. The last empty paragraph is not written. While editing, StarterKit's
 *    `trailingNode` adds an empty paragraph after a document that ends with a
 *    table or a footnote. It is a scaffold with no content; written out it
 *    became a blank line, then `&nbsp;` on the next round trip, one more per
 *    save. Only the last one is dropped: an empty paragraph in the middle was
 *    left by the writer.
 * 2. When the first block is a table, its leading "\n" is dropped
 *    (`renderTableToMarkdown` always adds one). Any other leading "\n" is an
 *    empty paragraph the writer put there; dropping it would remove one per
 *    normalization, so it is kept.
 */
export const Doc = Node.create({
  name: "doc",
  topNode: true,
  content: "block+",

  renderMarkdown(node, h) {
    const children = node.content ?? [];
    const last = children[children.length - 1];
    const body =
      last !== undefined && isBlankParagraph(last)
        ? children.slice(0, -1)
        : children;
    const md = renderBlockSiblings(body, h);
    return body[0]?.type === "table" ? md.replace(/^\n/, "") : md;
  },
});

function isBlankParagraph(node: JSONContent): boolean {
  return node.type === "paragraph" && (node.content?.length ?? 0) === 0;
}

export interface EditorExtensionOptions {
  profile: EditorProfile;
  /**
   * Extensions added by the host (cloud adds custom emoji, the screen adds node
   * views). One with the same name as a built-in extension replaces it - that
   * is how the screen gives a node its view (`TrameriaTable.extend({
   * addNodeView })` keeps the name and the markdown mapping). Others are
   * appended. They are not part of `EDITOR_GENERATION`.
   */
  extensions?: readonly AnyExtension[];
  /**
   * Bound to a Y.Doc: StarterKit's undo history is turned off (the Y binding
   * has its own undo manager; two undo stacks fight each other).
   */
  collaboration?: boolean;
}

/**
 * Link schemes kept besides Tiptap's built-in ones (http(s), mailto, tel, ...).
 * The desktop app hands exactly these to the OS (desktop-shell navigation.ts).
 */
export const EXTRA_LINK_SCHEMES = ["x-apple.systempreferences"] as const;

/**
 * The extension list for a profile: what defines the schema and the markdown,
 * plus input rules and keys that need no live state. Every call returns fresh
 * instances (a live editor owns its own); headless code uses
 * {@link getEditorSchema} for one shared schema.
 *
 * StarterKit already contains Link (a separate Link extension would be a
 * duplicate) and keeps horizontal rules and strikethrough.
 */
export function createEditorExtensions(
  options: EditorExtensionOptions,
): AnyExtension[] {
  const base: AnyExtension[] = [
    StarterKit.configure({
      document: false,
      undoRedo: options.collaboration ? false : undefined,
      // The drop cursor: the default is 1px of the text color and is hard to
      // see; the accent color at 2px (it goes into an inline style, so `var()`
      // works and follows dark mode).
      dropcursor: { color: "var(--color-accent)", width: 2 },
      // Opening a link on click is decided by the screen (open while reading,
      // a link card while writing).
      // A link to macOS's settings (x-apple.systempreferences:) is kept; any
      // other scheme outside the built-in list loses its href. Not through
      // `protocols`: that also registers the scheme with linkify, which
      // refuses a scheme with a dot.
      link: {
        openOnClick: false,
        isAllowedUri: (url, ctx) =>
          EXTRA_LINK_SCHEMES.some((s) =>
            url.toLowerCase().startsWith(`${s}:`),
          ) || ctx.defaultValidate(url),
      },
      // Replaced by a version that parses large documents faster.
      orderedList: false,
      // Its own entry below, so the screen can replace it by name (a code
      // block with highlighting and a copy band; same node and markdown).
      codeBlock: false,
      // Replaced by one that reads text after a code block in an item.
      listItem: false,
    }),
    CodeBlock,
    TrameriaListItem,
    TrameriaOrderedList,
    Doc,
    TrameriaTable,
    TableRow,
    TrameriaTableCell,
    TrameriaTableHeader,
    // Its own extension so replacing the table does not add it twice.
    TableCellDrag,
    HtmlBreak,
    // The option line above a table (tokenizer only; merged after parsing).
    TableOptions,
    TrameriaTaskList,
    TrameriaTaskItem,
    TaskListInput,
    HomeEnd,
    TrameriaFence,
    // Extension tokenizers run before marked's built-in ones, whatever the
    // order here, so toggles and callouts are read before the HTML block and
    // the blockquote.
    Details,
    DetailsSummary,
    DetailsContent,
    ToggleInput,
    Callout,
    CalloutTitle,
    CalloutContent,
    OpaqueBlock,
    FootnoteDef,
    FootnoteRef,
    TrameriaLink,
    TrameriaFile,
    TrameriaFileInline,
  ];
  // Global attributes are collected across all extensions, so the position
  // does not matter.
  if (options.profile === "document") base.push(BlockIdAttributes);

  const extra = options.extensions ?? [];
  const byName = new Map(extra.map((ext) => [ext.name, ext]));
  const replaced = new Set<string>();
  const list = base.map((ext) => {
    const replacement = byName.get(ext.name);
    if (!replacement) return ext;
    replaced.add(ext.name);
    return replacement;
  });
  for (const ext of extra) if (!replaced.has(ext.name)) list.push(ext);
  return list;
}

// Headless code relies on identity (`node.type === schema.nodes.paragraph`, and
// the Y binding's schema checks). `getSchema()` makes a new schema each call,
// so one extension list and schema per profile is built once and shared.
const bundles = new Map<
  EditorProfile,
  { extensions: AnyExtension[]; schema: Schema }
>();

function bundleOf(profile: EditorProfile) {
  let bundle = bundles.get(profile);
  if (!bundle) {
    const extensions = createEditorExtensions({ profile });
    bundle = { extensions, schema: getSchema(extensions) };
    bundles.set(profile, bundle);
  }
  return bundle;
}

/**
 * The headless schema of a profile. Memoized: every call returns the same
 * `Schema` (and node types) for the profile.
 */
export function getEditorSchema(profile: EditorProfile = "document"): Schema {
  return bundleOf(profile).schema;
}

/**
 * The extension instances behind {@link getEditorSchema}, for markdown.ts to
 * build its MarkdownManager from the same registrations. Not exported from the
 * package.
 */
export function getProfileExtensions(profile: EditorProfile): AnyExtension[] {
  return bundleOf(profile).extensions;
}
